import { useQuery } from '@tanstack/react-query';
import { TournamentDto, TournamentParticipant } from '@types';
import { AxiosContext } from '@/context/AxiosContext';
import { useContext } from 'react';

const normalizeParticipant = (
  participant?: TournamentParticipant | null
): TournamentParticipant | null => {
  if (!participant) {
    return null;
  }

  return {
    ...participant,
    tournamentWins: participant.tournamentWins ?? 0,
    tournamentTitle: participant.tournamentTitle ?? '',
    tournamentBadgeKey: participant.tournamentBadgeKey ?? '',
  };
};

export interface TournamentReportFilter {
  startDate?: string;
  endDate?: string;
  status?: string;
}

/**
 * Hook to fetch tournaments for reporting
 * Returns a list of tournaments within the specified date range
 */
export const useGetTournamentReports = (filter: TournamentReportFilter) => {
  const axios = useContext(AxiosContext);

  return useQuery<TournamentDto[]>({
    queryKey: ['tournament-reports', filter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filter.startDate) params.append('startDate', filter.startDate);
      if (filter.endDate) params.append('endDate', filter.endDate);
      if (filter.status) params.append('status', filter.status);

      const response = await axios.get<TournamentDto[]>(`/api/challenges/Tournament/reports?${params.toString()}`);
      return response.data.map((tournament) => ({
        ...tournament,
        winner: normalizeParticipant(tournament.winner),
        secondPlace: normalizeParticipant(tournament.secondPlace),
        thirdPlace: normalizeParticipant(tournament.thirdPlace),
      }));
    },
    enabled: !!(filter.startDate && filter.endDate),
    refetchInterval: 60000, // Refresh every 60 seconds
  });
};

export default useGetTournamentReports;
